import { isJobAssignedToWorker } from './workerUtils';
import { resolveHandoffPartnerRecord } from './handoffSettlementUtils';
import {
  jobHasWarranty,
  jobMatchesDeviceCategories,
  jobMatchesSearch
} from './queueFilterUtils';

export const EMPTY_ARCHIVE_EXTRA_FILTERS = {
  warranty: 'all',
  returnVisitOnly: false,
  serviceLocation: [],
  deviceCategory: [],
  fiscal: 'all'
};

export const hasActiveArchiveExtraFilters = (filters = {}) =>
  (filters.warranty && filters.warranty !== 'all') ||
  !!filters.returnVisitOnly ||
  (Array.isArray(filters.serviceLocation) && filters.serviceLocation.length > 0) ||
  (Array.isArray(filters.deviceCategory) && filters.deviceCategory.length > 0) ||
  (filters.fiscal && filters.fiscal !== 'all');

export const jobHasFiscalReceipt = (job) =>
  !!(job?.report?.fiscalReceiptNumber && String(job.report.fiscalReceiptNumber).trim());

export const jobMatchesArchiveExtraFilters = (job, filters = {}, { deviceCategories = [] } = {}) => {
  if (!job) return false;

  if (filters.warranty === 'yes' && !jobHasWarranty(job)) return false;
  if (filters.warranty === 'no' && jobHasWarranty(job)) return false;

  if (filters.returnVisitOnly && !(job.isReturnVisit || job.lastVisitDate)) return false;

  const locations = Array.isArray(filters.serviceLocation) ? filters.serviceLocation : [];
  if (locations.length > 0) {
    const jobLoc = job.serviceLocation === 'InWorkshop' ? 'InWorkshop' : 'OnSite';
    if (!locations.includes(jobLoc)) return false;
  }

  const categories = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : [];
  if (!jobMatchesDeviceCategories(job, categories, deviceCategories)) return false;

  if (filters.fiscal === 'yes' && !jobHasFiscalReceipt(job)) return false;
  if (filters.fiscal === 'no' && jobHasFiscalReceipt(job)) return false;

  return true;
};

export const jobMatchesArchiveSearch = (job, searchTerm) => jobMatchesSearch(job, searchTerm);


export const jobMatchesPartnerFilter = (job, partnerId, partners = [], sourceCompanyId = null) => {
  if (!job || !partnerId) return false;

  const pid = String(partnerId);
  const partner = partners.find((p) => String(p._id || p.id || '') === pid);
  if (!partner) return false;

  const assigned = String(job.assignedTo || '');
  if (assigned === `partner:${pid}` || assigned === pid) return true;

  if (sourceCompanyId) {
    const record = resolveHandoffPartnerRecord(job, partners, sourceCompanyId);
    if (record && String(record._id || record.id || '') === pid) return true;
  }

  const linkedId = partner.linkedCompanyId ? String(partner.linkedCompanyId) : null;
  if (linkedId) {
    if (String(job.companyId || '') === linkedId) return true;
    if (String(job.assignedPartnerCompanyId || '') === linkedId) return true;
  }

  const partnerName = String(partner.companyName || partner.name || '').trim();
  if (partnerName && assigned === partnerName) return true;

  return false;
};

export const jobMatchesWorkerFilter = (job, workerId, workers = [], teams = []) =>
  isJobAssignedToWorker(job, workerId, teams);

/**
 * Filtriranje po više radnika i/ili partnera.
 * Unutar grupe važi OR; ako su obe grupe izabrane, posao mora da odgovara bar jednoj grupi.
 */
export const applyAssigneeFilters = (
  jobs = [],
  { workerIds = [], partnerIds = [] } = {},
  { workers = [], partners = [], teams = [], sourceCompanyId = null } = {}
) => {
  const wIds = (workerIds || []).map(String).filter(Boolean);
  const pIds = (partnerIds || []).map(String).filter(Boolean);
  const hasWorkers = wIds.length > 0;
  const hasPartners = pIds.length > 0;

  if (!hasWorkers && !hasPartners) return jobs;

  return jobs.filter((job) => {
    const workerMatch =
      hasWorkers &&
      wIds.some((id) => jobMatchesWorkerFilter(job, id, workers, teams));
    const partnerMatch =
      hasPartners &&
      pIds.some((id) => jobMatchesPartnerFilter(job, id, partners, sourceCompanyId));

    if (hasWorkers && hasPartners) return workerMatch || partnerMatch;
    if (hasWorkers) return workerMatch;
    return partnerMatch;
  });
};
